import React, { useState } from 'react';
import { Image, Link } from '@packages/shared';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import clsx from 'clsx';
import {
  getFragmentData,
  type FragmentType,
  makeFragmentData,
} from '@packages/gql/generated/shopping';
import type { WishlistLineItemsFragmentFragment as WishlistLineItemsType } from '@packages/gql/generated/shopping/graphql';
import { WishlistLineItemsFragmentFragmentDoc } from '@packages/gql/generated/shopping/WishlistLineItemsFragmentFragmentDoc';
import type { GlycerinWishListProduct } from '@packages/tracking/src/types/types';
import type { BasketActionsFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketActionsFragmentFragmentDoc';
import { MiniDVLayer } from '@packages/modules/src/MiniDVLayer/MiniDVLayer';
import { ProductPowerSupplyFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductPowerSupplyFragmentFragmentDoc';
import { ProductPowerSupply } from '@packages/modules/src/ProductPowerSupply';
import { ProductPriceWrapper } from './ProductPriceWrapper';
import { BasketAction, DeleteAction, EditAction, SimilarProductAction } from '../Actions';
import { MoreMenu } from '../MoreMenu';
import { ActionBar } from '../ActionBar';
import { PowerEfficiency } from './PowerEfficiency';
import { addToBasketListViewStyleOverrides, inlineButtonStyle } from '../Actions/Actions.styles';
import { buildGlycerinWishlistProduct } from '../Tracking/buildGlycerinObject';

interface WishlistItemsProps {
  data?: FragmentType<typeof WishlistLineItemsFragmentFragmentDoc>;
}
/* GraphQL */ `
  fragment WishlistLineItemsFragment on WishlistLineItem {
    ...BasketActionsFragment
    sku
    akl
    imageUrl
    name
    isSoldOut
    deliveryMessage
    deliveryTextColor
    price {
      amount
      currency
      isStartPrice
      saving {
        type
        value
      }
      unit
      uvp
      value
      valueOld
      valueUnit
    }
    unspecified
    variation
    powerEfficiency {
      text
      iconText
      link
      icon
      dataSheet
    }
    powerSupply {
      chargerType
      maxPower
      minPower
      powerSupplyIncluded
    }
    extra {
      brand
    }
    ...SimilarProductsActionFragment
  }
`;

const availabilityStateColors: Record<
  NonNullable<WishlistLineItemsType['deliveryTextColor']>,
  string
> = {
  Success: 'text-color-success',
  Error: 'text-color-error',
  Warning: 'text-color-warning',
};
type LineItemActionsProps = {
  data: FragmentType<typeof BasketActionsFragmentFragmentDoc>;
  sku: WishlistLineItemsType['sku'];
  glycerinProduct: GlycerinWishListProduct;
  akl: WishlistLineItemsType['akl'];
};
const LineItemActions: React.FC<LineItemActionsProps> = ({ data, sku, glycerinProduct, akl }) => {
  const { isMobile } = useDeviceType();
  const [isMiniDVOpen, setIsMiniDVOpen] = useState(false);

  const children = [
    <BasketAction
      data={data}
      key="wishlistAddToBasket"
      overrideSx={{
        display: {
          xs: 'none',
          md: 'flex',
        },
        ...addToBasketListViewStyleOverrides,
      }}
      glycerinProduct={glycerinProduct}
    />,
    <EditAction
      mode="wishlist"
      key="wishlistEdit"
      glycerinProduct={glycerinProduct}
      onClick={() => setIsMiniDVOpen(true)}
    />,
    <DeleteAction
      mode="wishlist"
      key="wishlistDelete"
      lineItemSku={sku}
      glycerinProduct={glycerinProduct}
    />,
  ];

  const Wrapper = isMobile ? MoreMenu : ActionBar;

  return (
    <>
      <Wrapper>{isMobile ? children : children.reverse()}</Wrapper>
      <MiniDVLayer
        sku={sku}
        akl={akl}
        quantity={1}
        open={isMiniDVOpen}
        onClose={() => setIsMiniDVOpen(false)}
        mode="EIW"
      />
    </>
  );
};

export const WishlistLineItem: React.FC<WishlistItemsProps> = ({ data }) => {
  const fragmentData = getFragmentData(WishlistLineItemsFragmentFragmentDoc, data);

  const { language, localizeUrl } = useI18n();
  if (!fragmentData) return null;

  const {
    imageUrl,
    name,
    deliveryMessage,
    deliveryTextColor,
    price,
    sku,
    akl,
    isSoldOut,
    variation,
    powerEfficiency,
    powerSupply,
  } = fragmentData;
  const glycerinProduct = buildGlycerinWishlistProduct(fragmentData);
  const productLinkParams = new URLSearchParams({ sku }).toString();

  let powerSupplyFragment;
  if (powerSupply) {
    const tmp = { ...powerSupply, __typename: 'PowerSupply' as 'PowerSupply' };
    powerSupplyFragment = makeFragmentData(tmp, ProductPowerSupplyFragmentFragmentDoc);
  }

  return (
    <div className="shadow-color-grey-dark bg-color-background-base relative mb-1.5 w-full overflow-visible rounded-[3px] shadow-sm md:mb-2">
      <div className="hyphen-auto grid max-w-screen-xl grid-cols-[5rem,3fr,1fr] gap-x-1.5 gap-y-1 break-words px-1.5 pt-1.5 md:grid-cols-[7.5rem,3fr,1fr]">
        <div className="relative z-10 col-start-1 row-start-1 row-end-6 flex h-[80px] w-[80px] translate-x-0 scale-100 transform flex-col items-center justify-center md:my-2 md:h-[120px] md:w-[120px]">
          <Link href={localizeUrl(`/p/${akl}?${productLinkParams}`, language)}>
            <Image priority unoptimized alt={name} src={imageUrl} fill className="object-contain" />
          </Link>
        </div>
        <h5 className="text-size-body2 md:text-size-h5 col-span-2 col-start-2 row-span-1 mr-[28px] font-semibold md:col-span-1 md:mr-0">
          {name}
        </h5>
        <p className="text-size-body2 col-span-2 col-start-2 row-start-2 mr-[28px] overflow-hidden text-ellipsis whitespace-nowrap md:col-end-3 md:mr-0">
          {variation}
        </p>
        <p
          className={clsx(
            'text-size-body2 col-start-2 col-end-3',
            availabilityStateColors[deliveryTextColor ?? 'Success'],
          )}
        >
          {deliveryMessage}
        </p>
        {powerEfficiency && (
          <div className="col-start-2 row-start-4 row-end-5 md:row-start-4">
            <PowerEfficiency efficiency={powerEfficiency} />
          </div>
        )}
        {powerSupplyFragment && (
          <div className="col-start-2 row-start-5 row-end-6 mb-0.5 md:row-start-5">
            <ProductPowerSupply maskedData={powerSupplyFragment} showPowerSupplyMessage />
          </div>
        )}
        <div className="col-start-2 col-end-4 row-start-4 row-end-6 justify-self-end pt-1 md:col-start-3 md:row-span-3 md:row-start-1 md:pt-0">
          <ProductPriceWrapper isSoldOut={isSoldOut} price={price} />
        </div>
        <BasketAction
          overrideSx={inlineButtonStyle}
          data={fragmentData}
          glycerinProduct={glycerinProduct}
        />
        {isSoldOut && (
          <SimilarProductAction
            data={fragmentData}
            buttonProps={{
              sx: {
                gridColumn: { xs: '1 / span 3', md: '2 / span 1' },
                gridRow: { xs: '5 / span 1', md: 'auto' },
                mt: { xs: 1, md: 0 },
                mb: 2,
                maxWidth: { md: '50%' },
                // width: '100%',
              },
            }}
          />
        )}
      </div>
      <LineItemActions data={fragmentData} sku={sku} akl={akl} glycerinProduct={glycerinProduct} />
    </div>
  );
};
